/**
 * /field/wt — a well-testing hand's own jobs (GET /well-testing/field/my-jobs,
 * fieldiq-api#1245, WT-2).
 *
 * THE SELECTION IS THE API'S: the jobs whose crew the caller is on that are
 * still open. This screen only groups them, in the API's order. The field
 * surface is SNAKE_CASE (job_number, today_log_ids).
 *
 * The frame is Pressure Testing's field frame (PtFieldPage), imported, so the
 * verticals' field screens stay one shape.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, MapPin } from 'lucide-react';
import { useWtFieldJobs } from '../../../hooks/useWellTesting';
import { wtDay, wtJobStatusSkin } from '../../../lib/well-testing';
import type { WtFieldJob } from '../../../types/well-testing';
import { PtFieldBadge, PtFieldCard, PtFieldEmpty, PtFieldPage, PtFieldQueryState, PtFieldSectionTitle } from '../../pressure-testing/field/PtFieldBits';
import { WT_SOUR_SKIN } from '../WtBits';

function todayLine(job: WtFieldJob): React.ReactNode {
  const ids = job.today_log_ids;
  const written = [ids?.day ? 'day' : null, ids?.night ? 'night' : null].filter(Boolean) as string[];
  if (written.length > 0)
    return (
      <>
        <CheckCircle2 className="size-3.5 text-fi-green" aria-hidden="true" />
        Today&apos;s {written.join(' and ')} log written
      </>
    );
  return job.status === 'scheduled' ? `Starts ${wtDay(job.planned_start_on)}` : 'No log yet today';
}

function JobCard({ job }: { job: WtFieldJob }) {
  return (
    <Link to={`/field/wt/jobs/${job.id}`} className="block" data-testid="wt-my-job">
      <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
            <PtFieldBadge skin={wtJobStatusSkin(job.status)} />
            {job.well_sour && <PtFieldBadge skin={WT_SOUR_SKIN} />}
          </div>
          <p className="mt-1 truncate text-base font-bold text-white">{job.customer_name || '—'}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
            <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
            {job.well_name || '—'}
            {job.well_uwi || job.well_api_number ? ` · ${job.well_uwi || job.well_api_number}` : ''}
          </p>
          <p className="mt-1 flex items-center gap-1 text-xs text-fieldiq-muted">{todayLine(job)}</p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

const ON_SITE = new Set(['rigging_up', 'flowing', 'rigging_down']);

export default function WtMyJobsScreen() {
  const query = useWtFieldJobs();
  const jobs = query.data?.data ?? [];
  const groups = [
    { key: 'on_site', title: 'On site', jobs: jobs.filter((j) => ON_SITE.has(j.status)) },
    { key: 'scheduled', title: 'Scheduled', jobs: jobs.filter((j) => j.status === 'scheduled') },
    { key: 'other', title: 'Other', jobs: jobs.filter((j) => !ON_SITE.has(j.status) && j.status !== 'scheduled') },
  ].filter((g) => g.jobs.length > 0);

  return (
    <PtFieldPage title="My WT Jobs" backTo="/field" testId="wt-my-jobs">
      <PtFieldQueryState query={query} subject="your well testing jobs">
        {groups.length === 0 ? (
          <PtFieldEmpty>You are not on the crew of any open well testing job. The office puts you on a job&apos;s crew.</PtFieldEmpty>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-labelledby={`wt-group-${g.key}`}>
              <PtFieldSectionTitle>
                <span id={`wt-group-${g.key}`}>
                  {g.title} · {g.jobs.length}
                </span>
              </PtFieldSectionTitle>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </section>
          ))
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
